import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Star, Award, Zap, Building, BookOpen, Phone, Users, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";

const packFeatures = {
  starter: [
    "1 Provider profile",
    "2 Training listings",
    "Basic analytics dashboard",
    "Standard support",
    "Provider verification badge",
    "Basic customization options"
  ],
  professional: [
    "5 Provider profiles",
    "10 Training listings",
    "Advanced analytics dashboard",
    "Priority email support",
    "Provider verification badge",
    "Advanced customization options",
    "Featured in search results",
    "Training promotion tools"
  ],
  enterprise: [
    "Unlimited provider profiles",
    "Unlimited training listings",
    "Enterprise analytics suite",
    "24/7 dedicated support",
    "Provider verification badge",
    "Full customization capabilities",
    "Premium search placement",
    "Marketing campaign tools",
    "API access for integration",
    "Custom branding options"
  ]
};

const PackCard = ({ 
  title, 
  price, 
  monthlyPrice, 
  yearlyPrice, 
  description, 
  features, 
  popular = false, 
  buttonText = "Get Started", 
  variant = "default",
  billingPeriod,
  icon
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  const handleSubscribe = () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to subscribe to this pack",
        variant: "destructive",
      });
      navigate("/signin");
      return;
    }
    
    // Here you would implement the subscription logic
    toast({
      title: "Subscription initiated",
      description: `You're subscribing to the ${title} pack`,
    });
    
    // For Enterprise plan, redirect to contact form
    if (variant === "enterprise") {
      navigate("/contact");
    }
  };

  // Fix: Remove unused price prop and use monthlyPrice/yearlyPrice directly
  const currentPrice = billingPeriod === "monthly" ? monthlyPrice : yearlyPrice;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="h-full"
    >
      <Card className={`h-full flex flex-col ${popular ? 'border-primary shadow-lg' : ''}`}>
        {popular && (
          <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
            <Badge variant="default" className="bg-primary text-white">Most Popular</Badge>
          </div>
        )}
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
          <div className="mt-4">
            <span className="text-3xl font-bold">{currentPrice}</span>
            {/* Fix: Don't show "/month" or "/year" for Custom pricing */}
            {currentPrice !== "Custom" && (
              <span className="text-muted-foreground ml-1">/{billingPeriod === "monthly" ? "month" : "year"}</span>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex-grow">
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter>
          <Button 
            onClick={handleSubscribe}
            className="w-full" 
            variant={popular ? "default" : "outline"}
          >
            {buttonText}
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

const Packs = () => {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleConsultationClick = () => {
    if (!isAuthenticated) {
      navigate("/signin?redirect=/contact");
    } else {
      navigate("/contact");
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl">
            Provider Subscription Plans
          </h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the perfect plan to showcase your training programs on our platform. Scale your offerings as your business grows.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <Tabs 
            defaultValue="monthly" 
            value={billingPeriod}
            onValueChange={setBillingPeriod}
            className="w-full max-w-md"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="yearly">Yearly <Badge variant="outline" className="ml-2 bg-green-100 text-green-800 border-green-200">Save 20%</Badge></TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          <PackCard
            price={billingPeriod === "monthly" ? "$49.99" : "$479.88"}
            title="Starter"
            description="Perfect for new providers testing the platform"
            monthlyPrice="$49.99"
            yearlyPrice="$479.88"
            features={packFeatures.starter}
            variant="default"
            billingPeriod={billingPeriod}
            icon={<Building className="h-5 w-5 text-blue-500" />}
          />
          <PackCard
            price={billingPeriod === "monthly" ? "$99.99" : "$959.88"}
            title="Professional"
            description="Ideal for established training providers"
            monthlyPrice="$99.99"
            yearlyPrice="$959.88"
            features={packFeatures.professional}
            popular={true}
            variant="pro"
            billingPeriod={billingPeriod}
            icon={<BookOpen className="h-5 w-5 text-amber-500" />}
          />
          <PackCard
            price="Custom"
            title="Enterprise"
            description="For large organizations with multiple providers"
            monthlyPrice="Custom"
            yearlyPrice="Custom"
            features={packFeatures.enterprise}
            variant="enterprise"
            buttonText="Contact Sales"
            billingPeriod={billingPeriod}
            icon={<Sparkles className="h-5 w-5 text-purple-500" />}
          />
        </div>

        <div className="mt-16 text-center">
          <div className="bg-primary/5 rounded-lg p-8 max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 flex items-center justify-center">
              <Phone className="mr-2 h-6 w-6 text-primary" />
              Need a Custom Solution?
            </h2>
            <p className="text-muted-foreground mb-6">
              We offer tailored packages for organizations with specific requirements. Our team will work with you to create a custom plan that fits your needs.
            </p>
            <Button size="lg" variant="default" onClick={handleConsultationClick}>
              Schedule a Consultation
            </Button>
          </div>
        </div>

        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto grid gap-6">
            <div className="bg-background rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-lg mb-2">Can I upgrade my plan later?</h3>
              <p className="text-muted-foreground">Yes, you can upgrade your plan at any time. Your billing will be prorated for the remainder of your billing cycle.</p>
            </div>
            <div className="bg-background rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-lg mb-2">How do I add more providers to my account?</h3>
              <p className="text-muted-foreground">With the Professional plan, you can add up to 5 providers. For more providers, you can upgrade to the Enterprise plan or contact us for a custom solution.</p>
            </div>
            <div className="bg-background rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-lg mb-2">What happens if I need more training listings?</h3>
              <p className="text-muted-foreground">If you need more training listings than your current plan allows, you can upgrade to a higher tier plan or contact our sales team for additional listing packages.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Packs;